// Story 10.4 — Helpers puros para presets de periodo (Hoy / 7d / 30d) y
// formateo de deltas. Aislado de TanStack Router para tests.

export type PeriodPreset = 'today' | '7d' | '30d' | 'custom';

export interface PeriodRange {
  since: Date;
  until: Date;
}

const PRESET_VALUES: ReadonlyArray<Exclude<PeriodPreset, 'custom'>> = ['today', '7d', '30d'];

export function isPreset(value: unknown): value is Exclude<PeriodPreset, 'custom'> {
  return typeof value === 'string' && (PRESET_VALUES as readonly string[]).includes(value);
}

export function presetToRange(
  preset: Exclude<PeriodPreset, 'custom'>,
  now: Date = new Date(),
): PeriodRange {
  const until = new Date(now);
  switch (preset) {
    case 'today': {
      const since = new Date(now);
      since.setUTCHours(0, 0, 0, 0);
      return { since, until };
    }
    case '7d':
      return { since: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), until };
    case '30d':
      return { since: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), until };
  }
}

/** Detecta qué preset cubre una ventana, o `'custom'`. */
export function detectPreset(
  since: string | undefined,
  until: string | undefined,
  now: Date = new Date(),
): PeriodPreset {
  if (!since && !until) return '7d';
  if (since && isPreset(since) && !until) return since;
  if (since && until) {
    const sinceMs = new Date(since).getTime();
    const untilMs = new Date(until).getTime();
    if (Number.isNaN(sinceMs) || Number.isNaN(untilMs)) return 'custom';
    const TOLERANCE_MS = 60 * 1000;
    for (const p of PRESET_VALUES) {
      const range = presetToRange(p, now);
      if (
        Math.abs(range.since.getTime() - sinceMs) < TOLERANCE_MS &&
        Math.abs(range.until.getTime() - untilMs) < TOLERANCE_MS
      ) {
        return p;
      }
    }
  }
  return 'custom';
}

/** Calcula porcentaje vs valor previo. Retorna `null` si previo es 0. */
export function deltaPercent(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return (current - previous) / previous;
}

export interface DeltaDisplay {
  /** Texto formateado: "+12%" o "-5%" o "—". */
  text: string;
  /** "up" / "down" / "neutral" para color y flechas. */
  trend: 'up' | 'down' | 'neutral';
}

export function formatDelta(absoluteDelta: number, previousValue: number): DeltaDisplay {
  if (previousValue === 0) {
    if (absoluteDelta === 0) return { text: '—', trend: 'neutral' };
    return {
      text: absoluteDelta > 0 ? '+nuevo' : '—',
      trend: absoluteDelta > 0 ? 'up' : 'neutral',
    };
  }
  const ratio = absoluteDelta / previousValue;
  const pct = Math.round(ratio * 100);
  if (pct === 0) return { text: '0%', trend: 'neutral' };
  if (pct > 0) return { text: `+${pct}%`, trend: 'up' };
  return { text: `${pct}%`, trend: 'down' };
}

export function formatLatencyMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = seconds / 60;
  return `${minutes.toFixed(1)} min`;
}

export function formatErrorRate(rate: number): string {
  if (!Number.isFinite(rate) || rate < 0) return '—';
  const pct = rate * 100;
  if (pct < 0.01 && pct > 0) return '<0.01%';
  return `${pct.toFixed(2)}%`;
}

/** Convierte el rango a `?since=&until=` ISO. Si es preset, retorna preset. */
export function rangeToSearch(
  preset: PeriodPreset,
  range: PeriodRange | null,
): {
  since?: string;
  until?: string;
} {
  if (preset === 'custom' && range) {
    return { since: range.since.toISOString(), until: range.until.toISOString() };
  }
  if (preset !== 'custom') return { since: preset };
  return {};
}
